import { useUserRole } from '@/hooks/useUserRole'
import { useState, useCallback } from 'react'
import { useUserDetail } from './useUserDetail'
import { useUserManagementEditPermissions } from './useUserManagementEditPermissions'
import { useUserManagementEditDialogs } from './useUserManagementEditDialogs'
import { useUserManagementEditForm } from './useUserManagementEditForm'
import { useUserManagementEditAccountActions } from './useUserManagementEditAccountActions'
import { useUserManagementEditRoles } from './useUserManagementEditRoles'
import { useUserManagementEditAssignProperties } from './useUserManagementEditAssignProperties'
import { useUserManagementEditImpersonate } from './useUserManagementEditImpersonate'
import {
  buildNotificationActionDetails,
  isLibertyUser,
  shouldShowNotificationSettings,
} from '../utils/editPageDerived'
import { formatUserEmail } from '../utils/userManagementFormatters'

export function useUserManagementEditPage(userId?: string) {
  const permissions = useUserManagementEditPermissions()
  const { user, loading, fetchUser, patchUser } = useUserDetail(userId ?? '')
  const dialogs = useUserManagementEditDialogs()

  const [notesRefreshKey, setNotesRefreshKey] = useState(0)
  const reloadNotes = useCallback(() => setNotesRefreshKey((k) => k + 1), [])

  const form = useUserManagementEditForm({
    userId,
    user,
    fetchUser,
    patchUser,
  })

  const accountActions = useUserManagementEditAccountActions({
    userId,
    user,
    fetchUser,
    patchUser,
    setConfirmOpen: dialogs.setConfirmOpen,
    reloadNotes,
  })

  const roles = useUserManagementEditRoles({
    userId,
    user,
    fetchUser,
    isAdmin: permissions.isAdmin,
    isCompanyRole: permissions.isCompanyRole,
    canRemoveRole: permissions.canRemoveRole,
    canImpersonateApplicant: permissions.canImpersonateApplicant,
    canImpersonatePmc: permissions.canImpersonatePmc,
    canSetFinMgr: permissions.canSetFinMgr,
    removingRoleId: dialogs.removingRoleId,
    setRemovingRoleId: dialogs.setRemovingRoleId,
    finMgrRoleId: dialogs.finMgrRoleId,
    setFinMgrRoleId: dialogs.setFinMgrRoleId,
    pendingSalesRole: dialogs.pendingSalesRole,
    setPendingSalesRole: dialogs.setPendingSalesRole,
    setConfirmOpen: dialogs.setConfirmOpen,
  })

  const assignProperties = useUserManagementEditAssignProperties(userId, user, fetchUser)
  const { handleImpersonate } = useUserManagementEditImpersonate()

  const hideForm = isLibertyUser(user) && !permissions.isLibertyRole
  const actionDetails = buildNotificationActionDetails(user)
  const showNotificationSettings = shouldShowNotificationSettings(user, permissions.isAdmin)

  const { user: currentUser } = useUserRole()
  const loggedInEmail = currentUser?.email ?? ''
  const canShowRegionalManagerSelfAssign =
    permissions.roleName === 'ROLE_REGIONAL_MANAGER' &&
    permissions.canSelfAssignProps &&
    !!loggedInEmail &&
    !!user?.email &&
    loggedInEmail === user.email

  const accountStatus = user?.account_status ?? 'Active'
  const isActive = accountStatus === 'Active'
  const hasEmail = !!formatUserEmail(user?.email)

  return {
    userId,
    user,
    loading,
    fetchUser,
    notesRefreshKey,
    ...permissions,
    ...dialogs,
    ...form,
    ...accountActions,
    ...roles,
    ...assignProperties,
    handleImpersonate,
    hideForm,
    actionDetails,
    showNotificationSettings,
    canShowRegionalManagerSelfAssign,
    accountStatus,
    isActive,
    hasEmail,
  }
}
